import { IsMobileDevice } from "source/app/domain/Device/Helpers/IsMobile";
import { DisplayToastrError } from "source/app/presentation/Shared/HandleError";
import { MathUtils } from "source/app/services/Math/MathUtils";
import { ISoundFile } from "source/app/services/Sound/ISoundFile";
import { ISoundFileOptions } from "source/app/services/Sound/ISoundFileOptions";
import { playingSoundFiles } from "source/app/services/Sound/PlayingSoundFiles";

export class HowlerSoundFile implements ISoundFile {
    private howl?: Howl;

    private abortController: AbortController = new AbortController();

    constructor(readonly filename: string) {}

    IsPlaying() {
        if (!this.howl) {
            return false;
        }

        return this.howl.playing();
    }

    Play(options?: ISoundFileOptions) {
        if (IsMobileDevice() && !options?.allowMobile) {
            return;
        }

        const isConcurrentPlaysAllowed = options?.allowConcurrent;

        if (!isConcurrentPlaysAllowed && playingSoundFiles.Contains(this)) {
            return;
        }

        const shouldStream =
            this.filename.startsWith(`http://`) ||
            this.filename.startsWith(`https://`);

        const howlAsync = shouldStream
            ? this.GetStreamHowlAsync(options?.onDownloadProgressUpdate)
            : this.GetSoundHowlAsync();

        howlAsync.then((howlFromPromise) => {
            if (!howlFromPromise) {
                return;
            }

            this.SetupExtendedHowl(howlFromPromise, options);
            this.howl!.play();
        });
    }

    Pause() {
        this.howl?.pause();
    }

    Resume() {
        this.howl?.play();
    }

    Stop() {
        if (!this.howl) {
            try {
                this.abortController.abort();
            } catch (error) {
                // Required
                console.error(error);
                return;
            }
        }

        if (!this.howl) {
            return;
        }

        this.howl.stop();
        this.howl.unload();
    }

    Seek() {
        return Number(this.howl?.seek() ?? 0);
    }

    Duration() {
        return this.howl?.duration() ?? 0;
    }

    volume(volume: number) {
        this.howl?.volume(volume);
    }

    Unload() {
        this.howl?.unload();
    }

    FadeIn(
        initialVolume: number,
        finalVolume: number,
        duration: number,
        callback?: () => void
    ) {
        if (IsMobileDevice() || !this.howl) {
            return;
        }

        const howl = this.howl!;
        const durationOfEachFadeMs = 100;
        const numberOfFades = duration / durationOfEachFadeMs;

        // Some kind of bug in howler prevents me from just using .volume() here
        // so I have to pass in initialVolume
        howl.volume(initialVolume);

        let currentVolume = initialVolume;

        const volumeChangePerFade =
            (finalVolume - initialVolume) / numberOfFades;

        const IncreaseVolume = () => {
            const newVolume = Math.min(
                currentVolume + volumeChangePerFade,
                finalVolume
            );

            currentVolume += volumeChangePerFade;

            // console.log(`FADE IN volume is now ${newVolume}`);
            howl.volume(newVolume);
        };

        const DoSetTimeout = (interval: number) =>
            setTimeout(IncreaseVolume, interval);

        for (let i = 0; i < numberOfFades; i++) {
            DoSetTimeout(durationOfEachFadeMs * i);
        }

        const AfterFadeIn = () => {
            howl.volume(finalVolume);

            if (callback) {
                callback();
            }
        };

        setTimeout(AfterFadeIn, duration);
    }

    FadeOut(finalVolume: number, duration: number, callback?: () => void) {
        if (IsMobileDevice() || !this.howl) {
            return;
        }

        const howl = this.howl!;
        const durationOfEachFadeMs = 100;
        const numberOfFades = duration / durationOfEachFadeMs;

        const initialVolume = howl.volume();

        const volumeChangePerFade =
            (initialVolume - finalVolume) / numberOfFades;

        const ReduceVolume = () => {
            const newVolume = Math.max(howl.volume() - volumeChangePerFade, 0);
            // console.log(`FADE OUT volume is now ${newVolume}`);
            howl.volume(newVolume);
        };

        const DoSetTimeout = (interval: number) =>
            setTimeout(ReduceVolume, interval);

        for (let i = 0; i < numberOfFades; i++) {
            DoSetTimeout(durationOfEachFadeMs * i);
        }

        const AfterFadeOut = () => {
            howl.volume(finalVolume);

            if (callback) {
                callback();
            }
        };

        setTimeout(AfterFadeOut, duration);
    }

    private async GetStreamHowlAsync(
        onDownloadProgressUpdate?: (progressPercent: number) => void
    ) {
        const { signal } = this.abortController;

        signal.addEventListener(`abort`, () => {
            console.log(
                `Abort signal received for the file '${this.filename}'`
            );
        });

        const response = await fetch(this.filename, { signal });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const totalLength = +response.headers.get("Content-Length")!;
        let receivedLength = 0;
        let lastLoggedPercentage = 0;

        const reader = response.body!.getReader();
        const chunks = [];
        let array: Uint8Array;

        while (true) {
            const result = await reader.read();

            if (this.abortController.signal.aborted) {
                return undefined;
            }

            if (result.done) {
                break;
            }

            array = result.value!;
            chunks.push(array);
            receivedLength += array.length;

            const percentageReceived = Math.floor(
                (receivedLength / totalLength) * 100
            );

            if (percentageReceived >= lastLoggedPercentage) {
                onDownloadProgressUpdate?.(percentageReceived);
                lastLoggedPercentage = percentageReceived;
            }
        }

        onDownloadProgressUpdate?.(100);

        const blob = new Blob(chunks);
        const url = URL.createObjectURL(blob);
        const howl = this.GetHowl(url, 1.0, true, false);

        return howl;
    }

    private async GetSoundHowlAsync() {
        const path = `/sounds/${this.filename}.mp3`;
        const volume = MathUtils.RandomNumberBetween(9, 10) / 10;
        const howl = this.GetHowl(path, volume, false, false);
        return howl;
    }

    private GetHowl(
        path: string,
        volume: number,
        loop: boolean,
        html5: boolean
    ) {
        try {
            return new Howl({
                src: path,
                volume,
                loop,
                html5,
                format: [`mp3`],
            });
        } catch (error) {
            throw new Error("Error playing sound file " + path + ": " + error);
        }
    }

    private SetupExtendedHowl(inputHowl: Howl, options?: ISoundFileOptions) {
        const howlExtended = inputHowl
            .on("play", () => {
                if (!options?.allowConcurrent) {
                    playingSoundFiles.Add(this);
                }

                if (options?.onPlay) {
                    options.onPlay();
                }
            })
            .on("end", () => {
                if (!options?.allowConcurrent) {
                    playingSoundFiles.Remove(this);
                }

                if (options?.onEnd) {
                    options.onEnd();
                }
            })
            .on("loaderror", () => {
                var errorMessage = `Cannot load the sound at '${this.filename}'`;
                DisplayToastrError(errorMessage, "Error loading sound");
                console.error(errorMessage);
            });

        howlExtended.volume(options?.volume ?? 1);
        howlExtended.rate(options?.rate ?? 1);
        howlExtended.loop(options?.loop ?? false);
        this.howl = howlExtended;
        return howlExtended;
    }
}
